import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  output,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SvgIconComponent } from 'angular-svg-icon';
import { ChecklistHttpService } from '../../../../services/checklist-http.service';
import { OfflineStorageService } from '../../../../services/offline/offline-storage.service';

@Component({
  selector: 'app-image-thumbnail',
  standalone: true,
  imports: [SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="relative w-full aspect-square rounded-lg overflow-hidden cursor-pointer"
      tabindex="0"
      role="button"
      (click)="onView()"
      (keydown.enter)="onView()">
      @if (loading) {
        <div class="w-full h-full rounded-lg bg-tertiary animate-pulse"></div>
      } @else if (thumbnailUrl) {
        <img
          [src]="thumbnailUrl"
          alt="Thumbnail"
          class="w-full h-full object-cover night:brightness-50" />
      }
      @if (isQueuedForUpload()) {
        <span
          class="absolute bottom-1 left-1 z-10 rounded bg-black/70 px-1.5 py-0.5 text-2xs font-medium leading-4 text-white">
          Queued for upload
        </span>
      }
      @if (!readOnly()) {
        <button
          type="button"
          class="absolute top-1 right-1 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-primary bg-primary shadow hover:bg-secondary_hover"
          (click)="onRemove($event)">
          <svg-icon
            src="/assets/svg/x-close.svg"
            svgClass="w-3.5 h-3.5"
            class="text-secondary" />
        </button>
      }
    </div>
  `,
})
export class ImageThumbnailComponent implements OnInit, OnDestroy {
  readonly fileId = input.required<string>();
  readonly readOnly = input(false);

  readonly remove = output<void>();
  readonly view = output<void>();

  loading = true;
  thumbnailUrl: string | null = null;

  private readonly http = inject(ChecklistHttpService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private destroyed = false;
  private loadGeneration = 0;

  ngOnInit(): void {
    void this.loadThumbnail();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.revokeThumbnailUrl();
  }

  onView(): void {
    this.view.emit();
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit();
  }

  isQueuedForUpload(): boolean {
    return this.offlineStorage.isUploadQueued(this.fileId());
  }

  private async loadThumbnail(): Promise<void> {
    const generation = ++this.loadGeneration;
    const fileId = this.fileId();

    this.loading = true;
    this.revokeThumbnailUrl();
    this.cdr.markForCheck();

    const cachedBlob = await this.getCachedBlob(fileId);
    if (!this.isCurrentLoad(fileId, generation)) {
      return;
    }

    if (cachedBlob) {
      this.setThumbnailBlob(cachedBlob);

      const cachedUploadConfirmed = await this.isCachedUploadConfirmed(fileId);
      if (!this.isCurrentLoad(fileId, generation)) {
        return;
      }

      if (!cachedUploadConfirmed) {
        return;
      }

      await this.loadServerThumbnail(fileId, generation, true);
      return;
    }

    await this.loadServerThumbnail(fileId, generation, false);
  }

  private async getCachedBlob(fileId: string): Promise<Blob | null> {
    try {
      return await this.offlineStorage.getBlob(fileId);
    } catch {
      return null;
    }
  }

  private async isCachedUploadConfirmed(fileId: string): Promise<boolean> {
    try {
      return await this.offlineStorage.isBlobUploadConfirmed(fileId);
    } catch {
      return false;
    }
  }

  private async loadServerThumbnail(
    fileId: string,
    generation: number,
    evictConfirmedCacheOnSuccess: boolean
  ): Promise<void> {
    try {
      const blob = await firstValueFrom(this.http.getThumbnailBlob(fileId));
      if (!this.isCurrentLoad(fileId, generation)) {
        return;
      }

      this.setThumbnailBlob(blob);
      if (evictConfirmedCacheOnSuccess) {
        await this.offlineStorage.evictBlob(fileId);
      }
    } catch {
      if (this.isCurrentLoad(fileId, generation)) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private setThumbnailBlob(blob: Blob): void {
    this.revokeThumbnailUrl();
    this.thumbnailUrl = URL.createObjectURL(blob);
    this.loading = false;
    this.cdr.markForCheck();
  }

  private revokeThumbnailUrl(): void {
    if (this.thumbnailUrl) {
      URL.revokeObjectURL(this.thumbnailUrl);
      this.thumbnailUrl = null;
    }
  }

  private isCurrentLoad(fileId: string, generation: number): boolean {
    return (
      !this.destroyed &&
      this.fileId() === fileId &&
      this.loadGeneration === generation
    );
  }
}
